// AI Money Mentor — kid-friendly financial literacy tab for ages 9-15.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Sparkles, Award, Flame, BookOpen, Bot, HelpCircle, Send,
  Trophy, CheckCircle2, XCircle, Loader2, RefreshCw,
} from "lucide-react";
import { Card } from "@/components/primitives";
import { API } from "@/lib/budget";

// ---------- helpers ----------
const ACCENT = "#5F7161";
const SOFT_BG = "#F4F6F1";

const xpToLevel = (xp) => 1 + Math.floor((xp || 0) / 100);
const xpInLevel = (xp) => (xp || 0) % 100;

// ---------- Profile header (XP / Level / Streak / Badges) ----------
const ProfileHeader = ({ profile, loading }) => {
  const xp = profile?.xp || 0;
  const level = profile?.level || xpToLevel(xp);
  const inLevel = xpInLevel(xp);
  const streak = profile?.streak_days || 0;
  const badges = profile?.badges || [];
  return (
    <Card className="p-6 md:col-span-12" testId="mentor-profile-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#5F7161] text-white flex items-center justify-center shadow-sm">
            <Bot size={26} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Money Mentor</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              Hey learner! Ready to level up?
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div data-testid="mentor-level-pill"
               className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5F716114] border border-[#5F716133] text-[#5F7161]">
            <Trophy size={13} />
            <span className="text-xs font-medium">Level <b data-testid="mentor-level-value">{level}</b></span>
          </div>
          <div data-testid="mentor-xp-pill"
               className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E2B35B14] border border-[#E2B35B55] text-[#8A5A1A]">
            <Sparkles size={13} />
            <span className="text-xs font-medium"><b data-testid="mentor-xp-value">{xp}</b> XP</span>
          </div>
          <div data-testid="mentor-streak-pill"
               className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E27D6014] border border-[#E27D6055] text-[#B8593E]">
            <Flame size={13} />
            <span className="text-xs font-medium"><b data-testid="mentor-streak-value">{streak}</b> day streak</span>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#5A5C58] font-medium">
            Progress to Level {level + 1}
          </span>
          <span className="text-xs font-mono text-[#5A5C58]">{inLevel}/100 XP</span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-[#EFEDE7] overflow-hidden">
          <div
            data-testid="mentor-xp-progress"
            className="h-full rounded-full bg-gradient-to-r from-[#6B8E23] to-[#5F7161] transition-all duration-500"
            style={{ width: `${inLevel}%` }}
          />
        </div>
      </div>

      {badges.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="mentor-badges-row">
          <Award size={14} className="text-[#5F7161]" />
          {badges.map((b) => (
            <span key={b} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#D8D6D0] text-[#1A1C1A]">
              {b}
            </span>
          ))}
        </div>
      )}

      {loading && (
        <p className="text-xs text-[#5A5C58] mt-3 italic">syncing your progress…</p>
      )}
    </Card>
  );
};

// ---------- Today's Lesson ----------
const TodayLesson = ({ onAward }) => {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [completed, setCompleted] = useState(false);

  const fetchLesson = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/mentor/lesson/today`, { timeout: 60000 });
      setLesson(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not load today's lesson.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLesson(); }, [fetchLesson]);

  const handlePick = useCallback(async (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    const isCorrect = idx === lesson?.quiz?.correct_index;
    if (isCorrect && !completed) {
      setCompleted(true);
      try {
        await onAward({ xp_delta: 20, badge: "First Lesson" });
      } catch (_) { /* ignore */ }
    }
  }, [picked, lesson, completed, onAward]);

  return (
    <Card className="p-6 md:col-span-7" testId="mentor-lesson-card">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen size={16} className="text-[#5F7161]" />
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Today&apos;s Lesson</p>
      </div>
      {loading && (
        <div className="h-[260px] flex items-center justify-center text-[#5A5C58] text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading today&apos;s lesson…
        </div>
      )}
      {error && !loading && (
        <div className="p-4 rounded-xl bg-[#FDECEA] border border-[#F6C5BD] text-[#8A1F11] text-sm">
          {error}
          <button onClick={fetchLesson} className="ml-2 underline" data-testid="mentor-lesson-retry">retry</button>
        </div>
      )}
      {!loading && !error && lesson && (
        <>
          <h3 className="font-display text-2xl font-semibold leading-tight" data-testid="mentor-lesson-title">
            {lesson.title}
          </h3>
          <p className="text-xs text-[#5A5C58] mt-1">Topic · {lesson.topic}</p>

          <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-[#1A1C1A]">
            <p data-testid="mentor-lesson-explanation">{lesson.explanation}</p>
            {lesson.example && (
              <div className="p-4 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA]">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A5C58] font-medium mb-1">Example</p>
                <p>{lesson.example}</p>
              </div>
            )}
            {lesson.fun_fact && (
              <div className="p-3 rounded-xl bg-[#FFF4E5] border border-[#F4D9A7] text-[#8A5A1A] text-sm">
                <Sparkles size={12} className="inline mr-1.5 -mt-0.5" />
                <b>Fun fact:</b> {lesson.fun_fact}
              </div>
            )}
          </div>

          {lesson.quiz && (
            <div className="mt-5 pt-5 border-t border-[#E0E5DA]" data-testid="mentor-lesson-quiz">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A5C58] font-medium mb-2">Quick check</p>
              <p className="font-medium mb-3">{lesson.quiz.question}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {lesson.quiz.options.map((opt, i) => {
                  const isCorrect = i === lesson.quiz.correct_index;
                  const isPicked = picked === i;
                  let cls = "border-[#D8D6D0] hover:border-[#5F7161] hover:bg-[#F4F6F1]";
                  if (picked !== null) {
                    if (isCorrect) cls = "border-[#6B8E23] bg-[#EDF3E0] text-[#3D5B0F]";
                    else if (isPicked) cls = "border-[#E27D60] bg-[#FDECEA] text-[#8A1F11]";
                    else cls = "border-[#D8D6D0] opacity-60";
                  }
                  return (
                    <button
                      key={i}
                      data-testid={`mentor-lesson-option-${i}`}
                      onClick={() => handlePick(i)}
                      disabled={picked !== null}
                      className={`text-left text-sm px-4 py-3 rounded-xl border transition-all ${cls}`}
                    >
                      <span className="font-mono text-xs text-[#5A5C58] mr-2">{String.fromCharCode(65 + i)}.</span>
                      {opt}
                      {picked !== null && isCorrect && <CheckCircle2 className="inline ml-2 -mt-0.5" size={14} />}
                      {picked !== null && isPicked && !isCorrect && <XCircle className="inline ml-2 -mt-0.5" size={14} />}
                    </button>
                  );
                })}
              </div>
              {picked !== null && (
                <p
                  data-testid="mentor-lesson-feedback"
                  className={`mt-3 text-sm ${picked === lesson.quiz.correct_index ? "text-[#3D5B0F]" : "text-[#B8593E]"}`}
                >
                  {picked === lesson.quiz.correct_index ? "Correct! +20 XP " : "Not quite — "}
                  {lesson.quiz.explanation}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

// ---------- Ask AI chat ----------
const AskAI = () => {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm your Money Mentor. Ask me anything about money — saving, spending, allowance, anything!" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || busy) return;
    setError(null);
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/mentor/chat`, {
        message: q, session_id: sessionId,
      }, { timeout: 60000 });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Mentor is taking a break. Try again.");
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const examples = [
    "What's compound interest?",
    "How do I save for a bike?",
    "Why is a piggy bank a good idea?",
  ];

  return (
    <Card className="p-6 md:col-span-5 flex flex-col" testId="mentor-chat-card">
      <div className="flex items-center gap-2 mb-2">
        <Bot size={16} className="text-[#5F7161]" />
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Ask the Mentor</p>
      </div>
      <h3 className="font-display text-xl font-semibold leading-tight mb-3">Got a money question?</h3>

      <div
        data-testid="mentor-chat-thread"
        className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[360px] min-h-[200px]"
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={`mentor-chat-msg-${m.role}`}
              className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#5F7161] text-white rounded-br-md"
                  : "bg-[#F4F6F1] border border-[#E0E5DA] text-[#1A1C1A] rounded-bl-md"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="mentor-chat-typing">
            <div className="px-3.5 py-2.5 rounded-2xl bg-[#F4F6F1] border border-[#E0E5DA] text-[#5A5C58] text-sm">
              <Loader2 className="inline animate-spin mr-2" size={14} /> thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && (
        <p className="mt-2 text-xs text-[#B8593E]" data-testid="mentor-chat-error">{error}</p>
      )}

      {messages.length <= 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {examples.map((ex) => (
            <button
              key={ex}
              onClick={() => setInput(ex)}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#D8D6D0] hover:border-[#5F7161] hover:bg-[#F4F6F1] text-[#5A5C58] transition-colors"
              data-testid={`mentor-chat-suggestion-${ex.slice(0,10)}`}
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          data-testid="mentor-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          placeholder="Type a money question…"
          className="flex-1 resize-none bg-white border border-[#D8D6D0] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161] transition-all"
        />
        <button
          data-testid="mentor-chat-send"
          onClick={send}
          disabled={busy || !input.trim()}
          className="bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </div>
    </Card>
  );
};

// ---------- Daily Quiz ----------
const DailyQuiz = ({ onAward }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({}); // {idx: chosen}
  const [submitted, setSubmitted] = useState(false);
  const [awarded, setAwarded] = useState(false);

  const fetchQuiz = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/mentor/quiz/today`, { timeout: 60000 });
      setQuiz(data);
      setAnswers({});
      setSubmitted(false);
      setAwarded(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not load today's quiz.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuiz(); }, [fetchQuiz]);

  const total = quiz?.questions?.length || 0;
  const answered = Object.keys(answers).length;
  const correctCount = useMemo(() => {
    if (!quiz?.questions) return 0;
    return quiz.questions.reduce((s, q, i) => s + (answers[i] === q.correct_index ? 1 : 0), 0);
  }, [quiz, answers]);

  const handleSubmit = useCallback(async () => {
    if (submitted || answered < total) return;
    setSubmitted(true);
    const xp = correctCount * 10 + (correctCount === total ? 20 : 0);
    if (xp > 0 && !awarded) {
      setAwarded(true);
      try {
        await onAward({
          xp_delta: xp,
          badge: correctCount === total ? "Quiz Champion" : null,
        });
      } catch (_) { /* ignore */ }
    }
  }, [submitted, answered, total, correctCount, awarded, onAward]);

  return (
    <Card className="p-6 md:col-span-12" testId="mentor-quiz-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <HelpCircle size={16} className="text-[#5F7161]" />
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Daily Quiz</p>
        </div>
        <button
          data-testid="mentor-quiz-refresh"
          onClick={fetchQuiz}
          className="text-xs text-[#5F7161] hover:text-[#4E5C50] flex items-center gap-1.5"
          title="Reload today's quiz"
        >
          <RefreshCw size={12} /> Reload
        </button>
      </div>
      <h3 className="font-display text-xl font-semibold leading-tight mb-1">
        Test your money smarts
      </h3>
      <p className="text-sm text-[#5A5C58] mb-4">
        {quiz?.topic ? <>Topic · <b>{quiz.topic}</b></> : "Five quick questions. 10 XP per correct answer."}
      </p>

      {loading && (
        <div className="py-10 flex items-center justify-center text-[#5A5C58] text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading today&apos;s quiz…
        </div>
      )}
      {error && !loading && (
        <div className="p-4 rounded-xl bg-[#FDECEA] border border-[#F6C5BD] text-[#8A1F11] text-sm">
          {error}
          <button onClick={fetchQuiz} className="ml-2 underline" data-testid="mentor-quiz-retry">retry</button>
        </div>
      )}
      {!loading && !error && quiz?.questions && (
        <>
          <div className="space-y-5">
            {quiz.questions.map((q, qi) => {
              const picked = answers[qi];
              return (
                <div key={qi} data-testid={`mentor-quiz-q-${qi}`}>
                  <p className="font-medium mb-2">
                    <span className="text-[#5F7161] font-mono mr-2">Q{qi + 1}.</span>{q.q}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => {
                      const isPicked = picked === oi;
                      const isCorrect = oi === q.correct_index;
                      let cls = "border-[#D8D6D0] hover:border-[#5F7161] hover:bg-[#F4F6F1]";
                      if (submitted) {
                        if (isCorrect) cls = "border-[#6B8E23] bg-[#EDF3E0] text-[#3D5B0F]";
                        else if (isPicked) cls = "border-[#E27D60] bg-[#FDECEA] text-[#8A1F11]";
                        else cls = "border-[#D8D6D0] opacity-60";
                      } else if (isPicked) {
                        cls = "border-[#5F7161] bg-[#F4F6F1]";
                      }
                      return (
                        <button
                          key={oi}
                          data-testid={`mentor-quiz-${qi}-opt-${oi}`}
                          onClick={() => !submitted && setAnswers((a) => ({ ...a, [qi]: oi }))}
                          disabled={submitted}
                          className={`text-left text-sm px-3.5 py-2.5 rounded-xl border transition-all ${cls}`}
                        >
                          <span className="font-mono text-xs text-[#5A5C58] mr-2">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                          {submitted && isCorrect && <CheckCircle2 className="inline ml-2 -mt-0.5" size={14} />}
                          {submitted && isPicked && !isCorrect && <XCircle className="inline ml-2 -mt-0.5" size={14} />}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && q.explanation && (
                    <p className="mt-2 text-xs text-[#5A5C58] italic">{q.explanation}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-5 border-t border-[#E0E5DA] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-[#5A5C58]" data-testid="mentor-quiz-progress">
              {submitted
                ? <>You scored <b className="text-[#1A1C1A]" data-testid="mentor-quiz-score">{correctCount}</b> / {total} · +{correctCount * 10 + (correctCount === total ? 20 : 0)} XP</>
                : <>Answered <b>{answered}</b> / {total}</>}
            </p>
            {!submitted ? (
              <button
                data-testid="mentor-quiz-submit"
                onClick={handleSubmit}
                disabled={answered < total}
                className="bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white rounded-full px-5 py-2 text-sm font-medium transition-colors"
              >
                Submit answers
              </button>
            ) : (
              <button
                data-testid="mentor-quiz-reset"
                onClick={() => { setSubmitted(false); setAnswers({}); }}
                className="text-[#5F7161] hover:bg-[#E8E6E1]/60 rounded-full px-4 py-2 text-sm font-medium transition-colors"
              >
                Try again
              </button>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

// ---------- Weekly Challenges (read-only Phase 1 preview) ----------
const ChallengesPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/mentor/challenges`).then((r) => {
      if (alive) { setData(r.data); setLoading(false); }
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <Card className="p-6 md:col-span-12" testId="mentor-challenges-card">
      <div className="flex items-center gap-2 mb-2">
        <Flame size={16} className="text-[#E27D60]" />
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">This week&apos;s challenges</p>
      </div>
      <h3 className="font-display text-xl font-semibold leading-tight mb-3">Try 3 small wins</h3>
      {loading && <p className="text-sm text-[#5A5C58]">Loading challenges…</p>}
      {!loading && data?.challenges && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {data.challenges.map((c, i) => (
            <div
              key={c.id}
              data-testid={`mentor-challenge-${i}`}
              className="p-4 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA]"
            >
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A5C58] font-medium mb-1.5">
                Challenge {i + 1}
              </p>
              <p className="font-medium text-[#1A1C1A] leading-snug">{c.title}</p>
              <p className="text-xs text-[#5F7161] mt-2 font-mono">+{c.xp} XP</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ---------- Root component ----------
const MoneyMentor = () => {
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mentor/profile`);
      setProfile(data);
    } catch (_) { /* ignore */ }
    finally { setProfileLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const award = useCallback(async (payload) => {
    try {
      const { data } = await axios.post(`${API}/mentor/profile/award`, payload);
      setProfile(data);
    } catch (e) { console.error(e); }
  }, []);

  return (
    <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="money-mentor-section">
      <ProfileHeader profile={profile} loading={profileLoading} />
      <TodayLesson onAward={award} />
      <AskAI />
      <ChallengesPanel />
      <DailyQuiz onAward={award} />
    </section>
  );
};

export default MoneyMentor;
